import React, { useMemo, useState } from 'react';
import Graduate from '../assets/graduate.png';
import TextField from 'components/Form/TextField';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'components/Form/Button';
import {
  InitialSetupValues,
  SimpleGetStarted
} from '../types/get-started.type';
import {
  formatToMoney,
  isLeadWayEmail,
  passwordStrengthVariables
} from 'utils';
import { useParams } from 'react-router-dom';
import Payment from './Payment';
import Successful from './Successful';
import { services } from 'service';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import { useGetAPlan } from 'common/queries-and-mutations/payment';
import { Loader } from 'components/Loader';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

export default function SetupWithPlan() {
  const [page, setPage] = useState('create');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [formValue, setFormValues] =
    useState<SimpleGetStarted>(InitialSetupValues);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRegisterError, setIsRegisterError] = useState(false);

  const { planId } = useParams();

  const { data: selectedPlan, isFetching } = useGetAPlan({
    planId: Number(planId!)
  });

  const formik = useFormik({
    initialValues: {
      surname: '',
      otherName: '',
      email: '',
      phoneNumber: '',
      password: '',
      confirmPassword: ''
    },
    validationSchema: Yup.object({
      surname: Yup.string().required('Surname is required').nullable(),
      otherName: Yup.string().required('Other Names is required').nullable(),
      email: Yup.string()
        .email('Invalid email')
        .required('Email is required')
        .nullable()
        .test(
          'leadway-is-allowed',
          'Email is not allow or already in use',
          async function (value) {
            if (value) {
              if (isLeadWayEmail(value)) {
                return false;
              }
              return true;
            }
            return true;
          }
        ),
      phoneNumber: Yup.string()
        .min(7, 'Phone number must be at least 7 digits')
        .required('Phone Number is required')
        .nullable(),
      password: Yup.string()
        .required('Password is required')
        .nullable()
        .matches(
          // eslint-disable-next-line no-useless-escape
          /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[!@#$%^&*()_+{}\[\]:;<>,.?~|]).{8,40}$/,
          'Password must have at least one uppercase, one lowercase, one number, one character and be at least 8 characters long'
        ),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Confirm password is required')
        .nullable()
    }),
    onSubmit: (values, { setFieldTouched }) => {
      for (const key in values) {
        setFieldTouched(key, true);
      }
      if (selectedPlan) {
        handleSubmit({
          ...values,
          planId: Number(planId),
          planAmount: selectedPlan.price,
          reference: ''
        });
      }
    }
  });

  const handleSubmit = (formValues: SimpleGetStarted) => {
    setFormValues({ ...formValues, planId: Number(planId) });
    setPage('payment');
  };

  const onPaymentSuccess = async (reference: string) => {
    setIsProcessing(true);
    //Register user here
    try {
      await services.manageUsersService.createUser({
        ...formValue,
        reference
      });
      setPage('');
      setPaymentSuccess(true);
      setIsRegisterError(false);
    } catch (error: any) {
      setIsRegisterError(true);
      toast.error(<Alert message={error.message} type="error" />);
    }
  };

  useMemo(() => {
    if (paymentSuccess) {
      setTimeout(() => {
        setAccountCreated(true);
        setPaymentSuccess(false);
      }, 6000);
    }
  }, [paymentSuccess]);

  if (isFetching) {
    return (
      <div className="mt-40 flex flex-col items-center justify-center">
        <Loader color={'#FF6606'} height={50} width={50} />
      </div>
    );
  }

  return (
    <div>
      {page === 'create' && (
        <div className="flex flex-col-reverse md:flex-row">
          <div className="mx-9 my-20 md:mx-20 md:mt-44 md:w-1/2">
            <p className="text-3xl font-medium text-[#353535]">
              Let’s Create Your Account
            </p>
            <p className="text-sm font-bold text-primary-900">
              Selected Plan: {selectedPlan?.planType}{' '}
              {formatToMoney(selectedPlan?.price)}
            </p>

            <form
              autoComplete="false"
              className="mt-10"
              onSubmit={formik.handleSubmit}
            >
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name="surname"
                    placeholder=""
                    value={formik.values.surname}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Surname"
                    error={formik.touched.surname ? formik.errors.surname : ''}
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    maxLength={50}
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name="otherName"
                    placeholder=""
                    value={formik.values.otherName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Other Names"
                    error={
                      formik.touched.otherName ? formik.errors.otherName : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    maxLength={100}
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name="email"
                    placeholder=""
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Email"
                    error={formik.touched.email ? formik.errors.email : ''}
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    maxLength={55}
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <PhoneInput
                    defaultCountry="NG"
                    placeholder="Enter phone number"
                    withCountryCallingCode
                    name="phoneNumber"
                    international
                    value={formik.values.phoneNumber}
                    onChange={val => formik.setFieldValue('phoneNumber', val)}
                    error={
                      formik.touched.phoneNumber && formik.values.phoneNumber
                        ? isValidPhoneNumber(formik.values.phoneNumber)
                          ? undefined
                          : 'Invalid phone number'
                        : 'Phone number required'
                    }
                    className="mt-6 rounded-2xl border-[1.5px] border-gray-300 p-1 px-2"
                    onBlur={formik.handleBlur}
                    limitMaxLength
                    maxLength={25}
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name="password"
                    type="password"
                    placeholder=""
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Password"
                    error={
                      formik.touched.password ? formik.errors.password : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    hasStrengthIndicator={
                      passwordStrengthVariables(formik.values)
                        .hasStrengthIndicator
                    }
                    totalStrengthConditions={
                      passwordStrengthVariables(formik.values)
                        .totalStrengthConditions
                    }
                    strengthConditionsFulfilled={
                      passwordStrengthVariables(formik.values)
                        .strengthConditionsFulfilled
                    }
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    type="password"
                    name="confirmPassword"
                    placeholder=""
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Confirm Password"
                    error={
                      formik.touched.confirmPassword
                        ? formik.errors.confirmPassword
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
              <Button
                variant="primary"
                size="lg"
                className="mt-5 w-[7rem] rounded p-[0.1rem]"
                label="Proceed"
                type="submit"
                disabled={isProcessing}
                loading={isProcessing}
              ></Button>
            </form>
          </div>
          <div className="md:w-1/2">
            <img
              src={Graduate}
              className="hidden h-auto w-full object-cover md:flex md:h-screen md:flex-col"
              alt="Graduate"
            />
          </div>
        </div>
      )}
      {page === 'payment' && (
        <Payment
          onSubmit={onPaymentSuccess}
          formValue={formValue}
          isProcessing={isProcessing}
          isRegisterError={isRegisterError}
        />
      )}
      {paymentSuccess && (
        <Successful
          title="Payment Successful"
          buttomText="Setting up your dashboard"
          useCustomCss={true}
        />
      )}
      {accountCreated && (
        <Successful
          title="Account Created"
          showActionButton
          actionLink="/on-boarding"
          buttonText="Create your Will Now"
          useCustomCss={true}
        />
      )}
    </div>
  );
}
